import * as React from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { sb } from '@/lib/supabase'

/**
 * Realtime **Presence** — a shared, ephemeral key/value map over a channel.
 *
 * ## Why this exists when `useRealtime` already does
 *
 * `useRealtime` (postgres_changes) subscribes happily to this platform and then
 * delivers nothing, for the documented `wal2json` reason. Before building
 * follow-the-presenter on top of anything, all three Realtime mechanisms were
 * measured against a live tenant (`scripts/presence-check.mjs`):
 *
 * | mechanism         | result                                              |
 * |-------------------|-----------------------------------------------------|
 * | postgres_changes  | dead — no WAL decoding, the known platform gap      |
 * | broadcast         | **also dead** — a self-echo with `ack: true` times  |
 * |                   | out, so it is the relay, not the subscription       |
 * | **presence**      | **works, ~4ms cross-client, no rate limiting**      |
 *
 * Presence works *because* it never touches the WAL: the Realtime server holds
 * the state itself and diffs it to subscribers. That makes it the only live push
 * channel available here, and the reason slide position is carried as presence
 * state rather than as a row change or a broadcast message.
 *
 * Don't "simplify" this into a broadcast — it is not a stylistic choice, and the
 * failure mode is a silent one: the channel will subscribe and the room will
 * never move.
 *
 * ## Liveness is earned here too
 *
 * Same rule as `useRealtime`: `subscribed` means the socket joined, `live` means
 * state was actually **delivered**. Callers back their database poll off on
 * `live`, never on `subscribed` — see the note in `use-realtime.ts` for what
 * trusting the wrong one did to the Discussion page.
 *
 * ## Ephemerality is the trade
 *
 * Presence has no history. Whoever joins late learns nothing until the next
 * update, so anything that must survive a join is also written to the database —
 * for slide position that's `presentation_sessions.current_hash` (0020).
 */
export interface PresenceOptions<S> {
  /** Channel name. Everyone who should see each other must use the same one. */
  channel: string
  /**
   * This client's identity within the channel. One entry per key, so a stable
   * value (a user id) replaces that user's previous state instead of piling up
   * a second ghost attendee every time the component remounts.
   */
  key: string
  /** Published on join, and re-published whenever it changes. */
  state: S
  enabled?: boolean
}

export interface PresenceResult<S> {
  /** Everyone currently tracked, including this client. */
  peers: Array<S & { key: string }>
  /** The channel joined. Says nothing about delivery — see above. */
  subscribed: boolean
  /** Presence state has actually arrived. This is the one to trust. */
  live: boolean
}

export function usePresence<S extends Record<string, unknown>>({
  channel,
  key,
  state,
  enabled = true,
}: PresenceOptions<S>): PresenceResult<S> {
  const [peers, setPeers] = React.useState<Array<S & { key: string }>>([])
  const [subscribed, setSubscribed] = React.useState(false)
  const [live, setLive] = React.useState(false)

  const chRef = React.useRef<RealtimeChannel | null>(null)

  // The channel is torn down only when its identity changes, so the state a
  // re-render carries is read through a ref rather than a dependency.
  const stateRef = React.useRef(state)
  stateRef.current = state

  // Serialised, because `state` is a fresh object literal on every render and a
  // reference dependency would re-track dozens of times a second while the
  // presenter holds an arrow key down.
  const stateJson = JSON.stringify(state)

  React.useEffect(() => {
    if (!enabled) {
      setSubscribed(false)
      setLive(false)
      setPeers([])
      return
    }

    const ch = sb().channel(channel, { config: { presence: { key } } })
    chRef.current = ch

    const sync = () => {
      const raw = ch.presenceState<S>()
      setLive(true)
      setPeers(
        Object.entries(raw).flatMap(([k, entries]) =>
          (entries as S[]).map((entry) => ({ ...entry, key: k })),
        ),
      )
    }

    // 'sync' alone is enough — join/leave are followed by a sync carrying the
    // full state, and reacting to all three just re-renders the same thing
    // three times.
    ch.on('presence', { event: 'sync' }, sync)

    ch.subscribe((status) => {
      const up = status === 'SUBSCRIBED'
      setSubscribed(up)
      if (!up) {
        setLive(false)
        return
      }
      // Also re-tracks after a reconnect: the server drops this client's state
      // when the socket goes, and `status` arrives as SUBSCRIBED again on
      // rejoin. Without this the presenter would silently vanish from the room
      // after a network blip while their own screen kept working.
      void ch.track(stateRef.current)
    })

    return () => {
      chRef.current = null
      setSubscribed(false)
      setLive(false)
      setPeers([])
      void sb().removeChannel(ch)
    }
  }, [channel, key, enabled])

  React.useEffect(() => {
    if (!subscribed) return
    void chRef.current?.track(stateRef.current)
    // stateJson is the real dependency; stateRef.current is what gets sent.
  }, [stateJson, subscribed])

  return { peers, subscribed, live }
}
